import { useEffect, useState } from 'react';
import './App.css';

function App() {
  const [isPageLoading, setIsPageLoading] = useState(process.env.NODE_ENV !== 'test');
  const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }

    const finishLoading = () => setIsPageLoading(false);

    if (document.readyState === 'complete') {
      const timerId = window.setTimeout(finishLoading, 450);
      return () => window.clearTimeout(timerId);
    }

    window.addEventListener('load', finishLoading, { once: true });

    return () => {
      window.removeEventListener('load', finishLoading);
    };
  }, []);

  const heroSlides = [
    {
      id: '01',
      eyebrow: 'AfroSpark Logistics',
      titleTop: 'Moving Goods.',
      titleBottom: 'Building Futures.',
      text: 'AfroSpark delivers reliable road freight, warehousing, and cross-border logistics solutions across East and Central Africa.',
      primary: 'Explore Our Solutions',
      secondary: 'Get a Quote',
      position: 'center'
    },
    {
      id: '02',
      eyebrow: 'Operational Excellence',
      titleTop: 'Logistics Built',
      titleBottom: 'Around Your Cargo.',
      text: 'From specialized cargo movement to high-volume regional transport, we coordinate each step with precision and speed.',
      primary: 'View Services',
      secondary: 'Talk to Dispatch',
      position: 'center'
    },
    {
      id: '03',
      eyebrow: 'Regional Coverage',
      titleTop: 'Connecting Routes.',
      titleBottom: 'Accelerating Trade.',
      text: 'We support critical trade corridors with dependable fleet capacity, 24/7 tracking, and responsive on-ground teams.',
      primary: 'Track Shipment',
      secondary: 'Contact Operations',
      position: 'center'
    }
  ];

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setActiveSlide((previous) => (previous + 1) % heroSlides.length);
    }, 10000);

    return () => window.clearInterval(timerId);
  }, [heroSlides.length]);

  const goToPreviousSlide = () => {
    setActiveSlide((previous) => (previous - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToNextSlide = () => {
    setActiveSlide((previous) => (previous + 1) % heroSlides.length);
  };

  const currentSlide = heroSlides[activeSlide];
  const heroStyle = {
    // backgroundImage: "url('/logistics-unsplash.jpeg')",
    backgroundImage: "url('/afrospark-truck.png')",
    backgroundPosition: currentSlide.position
  };

  const services = [
    {
      title: 'Road Haulage',
      text: 'Specialized long-distance transport using our fleet of GPS-tracked, heavy-duty vehicles for continental logistics.',
      image: 'afrospark-truck.png'
    },
    {
      title: 'Warehousing',
      text: 'Strategic storage solutions with advanced inventory management and distribution services in secure facilities.',
      image: 'warehousing.jpeg'
    },
    {
      title: 'Freight Services',
      text: 'Seamless air and sea freight coordination including customs clearance and comprehensive documentation.',
      image: 'freight-services.jpeg'
    },
    {
      title: 'Last-Mile Delivery',
      text: "Fast, reliable, and trackable delivery services directly to your customer's doorstep across urban centers.",
      image: 'last-mile-delivery.jpg'
    },
    {
      title: 'Car Rentals',
      text: 'A versatile fleet of well-maintained vehicles for corporate and personal use, ensuring comfort and reliability.',
      image: 'car-rentals.jpeg'
    },
    {
      title: 'General Supply',
      text: 'Trusted procurement partner sourcing and delivering essential goods and materials for diverse business needs.',
      image: 'general-supply.png'
    },
    {
      title: 'Cleaning Services',
      text: 'Professional commercial and industrial cleaning solutions maintaining high standards of hygiene and presentation.',
      image: 'cleaning.jpeg'
    },    {
      title: 'Garage Services',
      text: 'Expert vehicle maintenance and repair ensuring both our fleet and client vehicles remain in peak condition.',
      image: 'garage.jpeg'
    }
  ];

  const advantage = [
    {
      value: '15+',
      title: 'Regional Reach',
      text: 'Operations across East and Central African routes.'
    },
    {
      value: '24/7',
      title: 'Real-Time Tracking',
      text: 'Live shipment visibility and proactive status updates.'
    },
    {
      value: '100+',
      title: 'Dedicated Fleet',
      text: 'Heavy-duty trucks and cargo vehicles under strict maintenance.'
    },
    {
      value: '1,000+',
      title: 'Expert Team',
      text: 'Experienced professionals across operations and dispatch.'
    }
  ];

  const industries = [
    {
      title: 'Mining and Resources',
      text: 'Heavy haulage and coordinated cargo movement for demanding project loads.'
    },
    {
      title: 'FMCG and Retail',
      text: 'Fast replenishment cycles powered by warehousing and route optimization.'
    },
    {
      title: 'Government Projects',
      text: 'Logistics planning for infrastructure and large-scale public operations.'
    }
  ];

  const news = [
    {
      tag: 'Operations',
      date: '22 Mar 2026',
      title: 'Digital proof-of-delivery now active on priority lanes'
    },
    {
      tag: 'Sustainability',
      date: '21 Mar 2026',
      title: 'Annual performance update shows reduced fleet emissions'
    },
    {
      tag: 'Expansion',
      date: '20 Mar 2026',
      title: 'New temperature-controlled hub strengthens regional delivery'
    }
  ];

  const clients = ['AB InBev', 'Puma Energy', 'Coca-Cola', 'Orica', 'Vivo Energy', 'TBL'];
  const clientStrip = [...clients, ...clients];

  const stats = [
    { label: 'Fleet Size', value: '10+ Trucks' },
    { label: 'Coverage', value: '5 Countries' },
    { label: 'Shipments', value: '100+ Deliveries' },
    { label: 'Support', value: '24/7 Operations' }
  ];

  if (isPageLoading) {
    return (
      <div className="app-loader" role="status" aria-live="polite" aria-label="Loading AfroSpark Logistics website">
        <img src="/company-logo.jpeg" alt="AfroSpark Limited" className="app-loader-logo" />
        <div className="loader-ring" aria-hidden="true" />
        {/* <div className="loader-track" aria-hidden="true">
          <span className="loader-progress" />
        </div>
        <p className="loader-text">Preparing your logistics dashboard...</p> */}
      </div>
    );
  }

  if (isUnderMaintenance) {
    return (
      <div className="maintenance-message" role="status" aria-live="polite" aria-label="AfroSpark Logistics website under maintenance">
        <div className="maintenance-content">
          <img src="/company-logo.jpeg" alt="AfroSpark Limited" className="maintenance-logo" />
          <h1>We'll be back soon!</h1>
          <p>Our website is currently undergoing scheduled maintenance. We apologize for any inconvenience and appreciate your patience.</p>
          <p>For urgent inquiries, please contact us</p>
          <div style={{ marginTop: '10px' }}>
            <span style={{ color: '#ff9800' }}> 0747096244 | afrosparkltd@gmail.com</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="site">
      <header className="topbar">
        <div className="brand">
          <img src="/company-logo.jpeg" alt="AfroSpark Limited logo" className="brand-logo" />
        </div>
        <nav className="nav-links" aria-label="Main navigation">
          <a href="#home">Home</a>
          <a href="#about">About us</a>
          <a href="#services">Services</a>
          {/* <a href="#advantage">Why AfroSpark</a> */}
          <a href="#contacts">Contacts</a>
          {/* <a href="#news">News</a>
          <a href="#fleet">Fleet</a> */}
        </nav>
        <button className="btn btn-solid" type="button">Get a Quote</button>
      </header>

      <main>
        <section className="hero" id="home" style={heroStyle}>
          <div className="hero-main-content">
            {/* <div>
              <button className="hero-nav" type="button" onClick={goToPreviousSlide} aria-label="Previous slide">
                &#8249;
              </button>
            </div> */}
            <div className="hero-content">

              <div className="hero-copy fade-up delay-1" key={currentSlide.id}>
                <p className="eyebrow fade-up delay-1">{currentSlide.eyebrow}</p>
                <h1 className="fade-up delay-2">
                  {currentSlide.titleTop}
                  <br />
                  {currentSlide.titleBottom}
                </h1>
                <p className="hero-text fade-up delay-3">{currentSlide.text}</p>
                <div className="hero-actions fade-up delay-4">
                  <button className="btn btn-solid" type="button">{currentSlide.primary}</button>
                  <button className="btn btn-outline" type="button">{currentSlide.secondary}</button>
                </div>
              </div>

              <div className="hero-stats" aria-label="Company key stats">
                {stats.map((item, index) => (
                  <div className={`hero-stat-item fade-up delay-${index + 1}`} key={item.label}>
                    <p style={{ color: '#ff9800' }}>{item.label}</p>
                    <h3>{item.value}</h3>
                  </div>
                ))}
              </div>
            </div>
            {/* <div>
              <button className="hero-nav" type="button" onClick={goToNextSlide} aria-label="Next slide">
                &#8250;
              </button>
            </div> */}
          </div>
          {/* <div className="hero-content">

            <div className="hero-copy fade-up delay-1" key={currentSlide.id}>
              <p className="eyebrow fade-up delay-1">{currentSlide.eyebrow}</p>
              <h1 className="fade-up delay-2">
                {currentSlide.titleTop}
                <br />
                {currentSlide.titleBottom}
              </h1>
              <p className="hero-text fade-up delay-3">{currentSlide.text}</p>
              <div className="hero-actions fade-up delay-4">
                <button className="btn btn-solid" type="button">{currentSlide.primary}</button>
                <button className="btn btn-outline" type="button">{currentSlide.secondary}</button>
              </div>
            </div>

            <section className="hero-stats" aria-label="Company key stats">
              {stats.map((item, index) => (
                <article className={`hero-stat-item fade-up delay-${index + 1}`} key={item.label}>
                  <p style={{ color: '#ff9800' }}>{item.label}</p>
                  <h3>{item.value}</h3>
                </article>
              ))}
            </section>
          </div>

          <div className="hero-controls" aria-label="Hero carousel controls">
            <button className="hero-nav" type="button" onClick={goToPreviousSlide} aria-label="Previous slide">
              &#8249;
            </button>
            <button className="hero-nav" type="button" onClick={goToNextSlide} aria-label="Next slide">
              &#8250;
            </button>
          </div> */}

          <div className="hero-dots" aria-label="Hero slide indicators">
            {heroSlides.map((slide, index) => (
              <button
                type="button"
                key={slide.titleTop}
                className={`hero-dot ${activeSlide === index ? 'is-active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          <div className="hero-nav-icons">
            <button className="hero-nav" type="button" onClick={goToPreviousSlide} aria-label="Previous slide">
              &#8249;
            </button>
            <button className="hero-nav" type="button" onClick={goToNextSlide} aria-label="Next slide">
              &#8250;
            </button>
          </div>
          <div className="hero-veil" aria-hidden="true" />
        </section>

        <section className="section" id="about">
          <div className="introduction-grid">
            {/* <div style={{ width: '70%' }}>
              <img src="/logistics-unsplash.jpeg" alt="AfroSpark Logistics truck" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
            </div> */}
            <div className="section-head intro-card" style={{ backgroundImage: "url('/afrospark-truck.png')", backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '8px', color: '#fff' }}>
              <div style={{ backgroundColor: 'rgba(15, 34, 83, 0.72)', padding: '2rem', borderRadius: '8px' }}>
                <p className="eyebrow fade-up delay-1">About AfroSpark</p>
                <h3 className="fade-up delay-2">Reliable Logistics Across East and Central Africa</h3>
                <p style={{ color: '#fff' }} className="fade-up delay-3">
                  Afrospark Limited is a premier logistics and transportation powerhouse,
                  strategically positioned to facilitate trade across the African continent.
                  With a focus on reliability, efficiency, and safety,
                  we provide end-to-end supply chain solutions tailored to modern business needs.
                </p>
              </div>

            </div>
            <div className="section-head intro-card" style={{ backgroundImage: "url('/afrospark-mission.webp')", backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '8px', color: '#fff' }}>
              <div style={{ backgroundColor: 'rgba(15, 34, 83, 0.72)', padding: '2rem', borderRadius: '8px', height: '100%' }}>
                <p className="eyebrow fade-up delay-1">Our Mission</p>
                <h3 className="fade-up delay-2">To Deliver Excellence in Every Mile</h3>
                <p style={{ color: '#fff' }} className="fade-up delay-3">
                  To ignite growth for our partners by providing seamless and innovative
                  logistics solutions that exceed expectations every time.
                </p>
              </div>

            </div>
            <div className="section-head intro-card" style={{ backgroundImage: "url('/afrospark-vision.jpeg')", backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: '8px', color: '#fff' }}>
              <div style={{ backgroundColor: 'rgba(15, 34, 83, 0.72)', padding: '2rem', borderRadius: '8px', height: '100%' }}>
                <p className="eyebrow fade-up delay-1">Our Vision</p>
                <h3 className="fade-up delay-2">To Be the Leading Logistics Provider in Africa</h3>
                <p style={{ color: '#fff' }} className="fade-up delay-3">
                  To become the most trusted and preferred logistics partner in Africa,
                  driving economic integration through superior service delivery.
                </p>
              </div>
            </div>
          </div>

        </section>

        <section className="section" id="services">
          <div className="section-head">
            <p className="eyebrow">Our Services</p>
            <h3>Logistics Built Around Your Cargo</h3>
            <p>
              From day-to-day freight to highly specialized moves, choose the level of support that matches your
              operation.
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <article className={`service-card fade-up delay-${index + 1}`} key={service.title}>
                <div className="service-image" style={{ backgroundImage: `url(${service.image})` }} aria-hidden="true" />
                <div className="service-veil" aria-hidden="true" />
                <div className="service-content">
                  <p className="service-chip">Core Service</p>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <div className="footer" id="contacts">
        <div className="footer-left">
          <p className="footer-brand" style={{ color: 'var(--accent)' }}>AfroSpark Limited</p>
          <address className="footer-address">
            Samora Plaza,Posta/Samora Street<br />
            Plot No.21343, P.O.BOX 1215<br />
            Ilala, Dar es salaam, Tanzania
          </address>
          <div className="footer-contact">
            <a href="tel:+25528250087">
              +255 (0) 747 096 244<br/>
              +255 (0) 745 282 103<br/>
              +255 (0) 762 122 151
            </a>
            <br />
            <a href="mailto:info@afrospark.co" style={{ color: 'var(--accent)' }}>info@afrospark.co.tz</a>
          </div>
        </div>
        <img src="/map.png" alt="AfroSpark Limited logo" className="footer-logo" />

        <div className="footer-right">
          <button className="btn btn-solid" type="button">Get a Quote</button>
          <p className="footer-hours">Mon–Fri, 08:00–18:00</p>
        </div>
      </div>
    </div>
  );
}

export default App;
